import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BlogShell from "./BlogShell";
import BlogCard from "./BlogCard";
import {
  buildBlogUrl,
  fetchBlogArticles,
  fetchBlogCategories,
  getCategoryBySlug,
  resolveBlogImageUrl,
  resolveCategorySlug,
} from "./blogApi";
import useSeoMetadata from "./useSeoMetadata";

function BlogCategory() {
  const { categorySlug } = useParams();
  const normalizedCategorySlug = resolveCategorySlug(categorySlug);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    Promise.all([
      fetchBlogCategories(),
      fetchBlogArticles({
        category: normalizedCategorySlug,
        page: 1,
        perPage: 100,
      }),
    ])
      .then(([nextCategories, listing]) => {
        if (cancelled) return;
        setCategories(nextCategories);
        setArticles(listing.articles);
        setLoading(false);
      })
      .catch((nextError) => {
        if (cancelled) return;
        setError(nextError);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [normalizedCategorySlug]);

  const category = useMemo(
    () => getCategoryBySlug(normalizedCategorySlug, categories),
    [categories, normalizedCategorySlug]
  );

  useSeoMetadata({
    title: category
      ? `${category.name}: статьи блога Энилекс.Контроль`
      : "Раздел блога Энилекс.Контроль",
    description:
      category?.description ||
      "Раздел тематических материалов Энилекс.Контроль про маркетплейсы, аналитику и операционную работу seller-команд.",
    canonicalUrl: buildBlogUrl(`/blog/category/${normalizedCategorySlug || ""}`),
    openGraph: {
      type: "website",
      title: category
        ? `${category.name}: статьи блога Энилекс.Контроль`
        : "Раздел блога Энилекс.Контроль",
      description:
        category?.description ||
        "Раздел тематических материалов Энилекс.Контроль про маркетплейсы, аналитику и операционную работу seller-команд.",
      image: resolveBlogImageUrl(),
      url: buildBlogUrl(`/blog/category/${normalizedCategorySlug || ""}`),
      siteName: "Энилекс.Контроль",
      locale: "ru_RU",
    },
    twitter: {
      card: "summary_large_image",
      title: category
        ? `${category.name}: статьи блога Энилекс.Контроль`
        : "Раздел блога Энилекс.Контроль",
      description:
        category?.description ||
        "Раздел тематических материалов Энилекс.Контроль про маркетплейсы, аналитику и операционную работу seller-команд.",
      image: resolveBlogImageUrl(),
    },
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name} - блог Энилекс.Контроль`,
            url: buildBlogUrl(`/blog/category/${category.slug}`),
            description: category.description,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Блог",
                item: buildBlogUrl("/blog"),
              },
              {
                "@type": "ListItem",
                position: 2,
                name: category.name,
                item: buildBlogUrl(`/blog/category/${category.slug}`),
              },
            ],
          },
        ]
      : [],
  });

  if (!loading && !category) {
    return (
      <BlogShell>
        <section className="blog-panel blog-empty">
          <h1>Раздел не найден</h1>
          <p>Проверьте ссылку или вернитесь к полному списку материалов.</p>
          <Link className="btn" to="/blog">
            Открыть блог
          </Link>
        </section>
      </BlogShell>
    );
  }

  return (
    <BlogShell>
      <nav
        className="breadcrumbs"
        aria-label="Breadcrumbs"
        data-testid="breadcrumbs"
      >
        <Link to="/blog">Главная блога</Link>
        <span className="breadcrumbs__separator">→</span>
        <Link to="/blog">Блог</Link>
        <span className="breadcrumbs__separator">→</span>
        <span className="breadcrumbs__current">
          {category?.name || "Раздел"}
        </span>
      </nav>

      <section className="blog-hero blog-panel">
        <div className="blog-hero__kicker">Раздел блога</div>
        <h1 className="blog-hero__title">{category?.name || "Раздел"}</h1>
        <p className="blog-hero__subtitle">
          {category?.description} Сейчас в этом тематическом слое {articles.length}{" "}
          материалов.
        </p>
        <div className="filter-chips" aria-label="Другие тематические разделы">
          {categories
            .filter((item) => item.slug !== "all")
            .map((item) => (
              <Link
                key={item.slug}
                className={`filter-chip${item.slug === category?.slug ? " active" : ""}`}
                to={`/blog/category/${item.slug}`}
              >
                {item.name}
                <span className="filter-chip__count">{item.count}</span>
              </Link>
            ))}
        </div>
      </section>

      {loading ? (
        <section className="blog-panel blog-status" aria-busy="true">
          <h2>Загружаем раздел</h2>
          <p>Собираем материалы и метаданные раздела.</p>
        </section>
      ) : null}

      {error ? (
        <section className="blog-panel blog-status blog-status--error">
          <h2>Не удалось загрузить раздел</h2>
          <p>Попробуйте обновить страницу.</p>
        </section>
      ) : null}

      {!loading && !error ? (
        <>
          <section className="blog-grid">
            {articles.map((article) => (
              <BlogCard article={article} key={article.slug} />
            ))}
          </section>

          {articles.length === 0 ? (
            <section className="blog-panel blog-empty">
              <h2>Пока нет материалов</h2>
              <p>Раздел готов для публикаций, но статьи еще не вышли.</p>
            </section>
          ) : null}
        </>
      ) : null}
    </BlogShell>
  );
}

export default BlogCategory;
